import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HamAccordionItemComponent } from '../../../../../aumico-components/src/lib/components/accordion-item/accordion-item.component';

import { WorkflowForm } from '@modeso/aumico-lib-cpmm-fe/src/lib/interfaces/workflow-form.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal';
import { distinctUntilChanged, Observable, of, Subject } from 'rxjs';
import { AumicoCoreProvider, NotificationType } from '../../../../../aumico-lib-core-fe/src/lib';
import { UpdateWorkflowSectionDto } from '../../dtos/update-workflow-section-dto.interface';
import { WorkflowDto } from '../../dtos/workflow-dto.interface';
import { WorkflowSectionDto } from '../../dtos/workflow-section-dto.interface';
import { SchedulePeriod } from '../../enums/schedule-period.enum';
import { TaskType } from '../../enums/task-type.enum';
import { WorkflowArea } from '../../enums/workflow-area.enum';
import { nonEmptyFormArrayValidator } from '../../validators/non-empty-array.validator';
import { uniqueSectionNamesForActiveType } from '../../validators/unique-section-names.validator';

@UntilDestroy()
@Component({
    selector: 'workflow-configurator',
    templateUrl: './workflow-configurator.component.html',
    styleUrls: ['./workflow-configurator.component.scss'],
})
export class WorkflowConfiguratorComponent implements OnInit, OnChanges {
    private autoScrollInterval: any = null;

    @Input() workflowDto?: WorkflowDto;
    @Input() area: WorkflowArea = WorkflowArea.MANDATE_MANAGEMENT;

    @Output() onEditSection = new EventEmitter<{ sectionId: string; sectionUpdateDto: UpdateWorkflowSectionDto }>();
    @Output() onWorkflowFormChange = new EventEmitter<WorkflowForm>();
    @Output() onWorkflowFormValidityChange = new EventEmitter<boolean>();

    openSectionDeletionConfirmationDialog = false;
    sectionToRemove: FormGroup | undefined;
    showEmptySectionConfirmationDialog = false;
    private saveSubject$: Subject<boolean>;

    form!: FormGroup;
    days = Array.from({ length: 31 }, (_, i) => ({
        id: i + 1,
        name: String(i + 1),
    }));
    months = Array.from({ length: 12 }, (_, i) => ({
        id: i + 1,
        name: new Intl.DateTimeFormat(this.i18NextService.language, { month: 'long' }).format(new Date(2000, i, 1)),
    }));
    periods = [
        {
            id: SchedulePeriod.CURRENT_PERIOD,
            name: this.i18NextService.t('aumico_cpmm_current_period') as string,
        },
        {
            id: SchedulePeriod.NEXT_PERIOD,
            name: this.i18NextService.t('aumico_cpmm_next_period') as string,
        },
    ];
    taskTypes = TaskType;

    sectionNameEditMode: { [key: number]: boolean } = {};
    activeTaskType: TaskType = TaskType.GENERAL;

    defaultSectionName: string = this.i18NextService.t('aumico_cpmm_set_section_name') as string;

    @ViewChildren('sectionNameInput') sectionNameInputs!: QueryList<ElementRef<HTMLInputElement>>;
    @ViewChildren('taskDescriptionInput') taskDescriptionInputs!: QueryList<ElementRef<HTMLInputElement>>;

    @ViewChildren(HamAccordionItemComponent)
    private accordions!: QueryList<HamAccordionItemComponent>;

    activeSectionIndex: number | null = null;

    sectionExpanded: boolean[] = [];

    attemptSaveWasClickedButNotSuccessful: boolean = false;

    constructor(
        private fb: FormBuilder,
        private i18NextService: I18NextService,
        private aumicoCoreProvider: AumicoCoreProvider,
    ) {
        this.initializeForm();
    }

    ngOnInit(): void { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['workflowDto'] && changes['workflowDto'].currentValue != null) {
            this.updateFormWithDto();
        }

        if (changes['area']) {
            this.form.patchValue({
                name:
                    this.area === WorkflowArea.MANDATE_MANAGEMENT
                        ? 'Mandate Management Workflow'
                        : this.workflowDto
                            ? this.workflowDto.name
                            : null,
                area: this.area,
            });
        }
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardShortcuts(event: KeyboardEvent) {
        // CTRL + N -> New Section
        if (event.ctrlKey && event.key.toLowerCase() === 'n') {
            event.preventDefault();
            this.addSection();
            return;
        }

        // CTRL + T -> MAC or ALT+T: Add Task
        if (event.ctrlKey && event.key.toLowerCase() === 't') {
            event.preventDefault();
            this.addTaskToActiveSection(this.sections.at(this.activeSectionIndex) as FormGroup);
            return;
        }
    }

    private initializeForm(): void {
        let workflowName: string | undefined;
        if (this.area === WorkflowArea.MANDATE_MANAGEMENT) {
            // Set a hardcoded name for the mandate management workflow
            workflowName = 'Mandate Management Workflow';
        }

        // Initial empty form
        this.form = this.fb.group({
            name: [workflowName, Validators.required],
            area: [this.area, Validators.required],
            sectionsToCreate: this.fb.array([], [uniqueSectionNamesForActiveType(this.activeTaskType)]),
        });

        this.listenToFormChanges();
    }

    private updateFormWithDto(): void {
        this.form.patchValue({
            name: this.workflowDto.name,
            area: this.workflowDto.area,
        });

        const sectionsArray = this.form.get('sectionsToCreate') as FormArray;
        sectionsArray.clear();

        this.workflowDto.sections.forEach((section) => {
            const tasks = this.workflowDto.workflowTasks.filter((t) => t.sectionId === section.id);

            const taskControls = tasks.map((task) =>
                this.fb.group({
                    workflowTaskId: [task.workflowTaskId],
                    description: [task.description, Validators.required],
                    taskType: [task.taskType, Validators.required],
                    scheduleDay: [
                        this.days.find((d) => d.id === task.scheduleDay),
                        this.area === WorkflowArea.MANDATE_MANAGEMENT ? Validators.required : null,
                    ],
                    scheduleMonth: [
                        this.months.find((m) => m.id === task.scheduleMonth),
                        this.area === WorkflowArea.MANDATE_MANAGEMENT ? Validators.required : null,
                    ],
                    schedulePeriod: [
                        this.periods.find((p) => p.id === task.schedulePeriod),
                        this.area === WorkflowArea.MANDATE_MANAGEMENT ? Validators.required : null,
                    ],
                }),
            );

            const sectionGroup = this.fb.group({
                name: [section.name, Validators.required],
                id: [section.id],
                workflowTasks: this.fb.array(taskControls),
            });

            sectionsArray.push(sectionGroup);
        });
    }

    get sections(): FormArray {
        return this.form.get('sectionsToCreate') as FormArray;
    }

    get sectionControls(): FormGroup[] {
        return this.sections.controls as FormGroup[];
    }

    public originalIndexOf(section: AbstractControl): number {
        return this.sections.controls.indexOf(section);
    }

    getFilteredTaskControls(section: FormGroup): FormGroup[] {
        const tasks = section.get('workflowTasks') as FormArray;
        return tasks.controls.filter(
            (task: AbstractControl) => task.get('taskType')?.value === this.activeTaskType,
        ) as FormGroup[];
    }

    getFilteredSectionControls(sections: FormArray): FormGroup[] {
        return sections.controls.filter((sectionControl: AbstractControl) => {
            const tasks = sectionControl.get('workflowTasks') as FormArray;

            // Return true if there are no tasks, or if at least one task matches the activeTaskType
            return (
                tasks.length === 0 ||
                tasks.controls.some(
                    (taskControl: AbstractControl) => taskControl.get('taskType')?.value === this.activeTaskType,
                )
            );
        }) as FormGroup[];
    }

    addSection(): void {
        this.sections.push(
            this.fb.group({
                name: [null, Validators.required],
                workflowTasks: this.fb.array([], nonEmptyFormArrayValidator),
            }),
        );
        // Set edit mode for the new section to true by default
        const newSectionIndex = this.sections.length - 1;
        this.sectionNameEditMode[newSectionIndex] = true;
        this.addTask(this.sections.at(newSectionIndex) as FormGroup);
        this.sectionExpanded[newSectionIndex] = true;

        setTimeout(() => {
            const lastInput = this.sectionNameInputs.last;
            if (lastInput) {
                lastInput.nativeElement.focus();
            }
        });
    }

    editSectionName(section: FormGroup, index: number): void {
        const sectionValue: WorkflowSectionDto = section.value;
        if (!sectionValue.name || sectionValue.name.trim().length === 0) {
            const nameControl = section.get('name');
            nameControl?.markAsTouched();
            nameControl?.setErrors({ required: true });
            return;
        }
        if (this.sectionNameEditMode[index]) {
            this.sectionNameEditMode[index] = false;
        } else {
            this.sectionNameEditMode[index] = true;
            if (sectionValue.name === this.defaultSectionName) {
                section.patchValue({ name: null });
            }
        }

        if (sectionValue.name == null || sectionValue.name == '') {
            sectionValue.name = this.defaultSectionName;
            section.patchValue({ name: this.defaultSectionName });
        }

        // If section is new or not yet saved(has no id), we don't edit the name we wait till we save the section with the whole workflow
        // Also don't save if section name input is in edit mode
        if (this.workflowDto == null || sectionValue.id == null || this.sectionNameEditMode[index]) {
            return;
        }
        this.onEditSection.emit({
            sectionId: sectionValue.id,
            sectionUpdateDto: {
                name: sectionValue.name.trim(),
            } as UpdateWorkflowSectionDto,
        });
    }

    addTask(section: FormGroup): void {
        const tasks = section.get('workflowTasks') as FormArray;
        tasks.push(
            this.fb.group({
                description: ['', Validators.required],
                taskType: [this.activeTaskType, Validators.required],
                scheduleDay: [null, this.area === WorkflowArea.MANDATE_MANAGEMENT ? Validators.required : null],
                scheduleMonth: [null, this.area === WorkflowArea.MANDATE_MANAGEMENT ? Validators.required : null],
                schedulePeriod: [null, this.area === WorkflowArea.MANDATE_MANAGEMENT ? Validators.required : null],
            }),
        );
        const sectionIndex = this.sections.controls.indexOf(section);
        setTimeout(() => {
            // Focus the input for the newly added task in the correct section
            const input = this.getTaskInput(sectionIndex, tasks.length - 1);
            if (input && tasks.length > 1) {
                input.nativeElement.focus();
            }
        });
    }

    addTaskToActiveSection(section: FormGroup): void {
        if (this.sections.length === 0) {
            return;
        }
        //no active section, so we set the last section as active
        if (this.activeSectionIndex === null) {
            this.activeSectionIndex = this.sections.length - 1;
        }
        this.addTask(this.sections.at(this.activeSectionIndex) as FormGroup);
    }

    removeSection(): void {
        const index = this.sections.controls.indexOf(this.sectionToRemove);
        if (index !== -1) {
            this.sections.removeAt(index);
        }

        this.openSectionDeletionConfirmationDialog = false;
        this.sectionToRemove = undefined;
    }

    onCancelSectionDeletion(): void {
        this.openSectionDeletionConfirmationDialog = false;
        this.sectionToRemove = undefined;
    }

    removeTask(section: FormGroup, taskIndex: number, sectionIndex: number): void {
        const tasks = section.get('workflowTasks') as FormArray;
        tasks.removeAt(taskIndex);
    }

    dropSection(event: CdkDragDrop<FormGroup[]>): void {
        const filteredSections = this.getFilteredSectionControls(this.sections);

        const previousSection = filteredSections[event.previousIndex];
        const currentSection = filteredSections[event.currentIndex];

        const previousIndex = this.originalIndexOf(previousSection);
        const currentIndex = this.originalIndexOf(currentSection);

        this.moveItemInFormArray(this.sections, previousIndex, currentIndex);
    }

    dropTask(event: CdkDragDrop<FormGroup[]>, section: FormGroup): void {
        const tasks = section.get('workflowTasks') as FormArray;
        const filteredTasks = this.getFilteredTaskControls(section);

        const previousTask = filteredTasks[event.previousIndex];
        const currentTask = filteredTasks[event.currentIndex];

        const previousIndex = tasks.controls.indexOf(previousTask);
        const currentIndex = tasks.controls.indexOf(currentTask);

        this.moveItemInFormArray(tasks, previousIndex, currentIndex);
    }

    moveItemInFormArray(formArray: FormArray, previousIndex: number, currentIndex: number): void {
        const item = formArray.at(previousIndex);
        formArray.removeAt(previousIndex);
        formArray.insert(currentIndex, item);
        this.emitWorkflowValue(this.form.value);
    }

    private listenToFormChanges(): void {
        this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                this.emitWorkflowValue(value);
            });

        this.form.statusChanges.pipe(untilDestroyed(this)).subscribe((status) => {
            const isValid = status === 'VALID';
            this.onWorkflowFormValidityChange.emit(isValid);
        });
    }

    private emitWorkflowValue(workflowFormValue: WorkflowForm): void {
        // we need to emit the WorkflowForm, even if it is not valid, because the parent component needs to know about the changes (Pop-Up Discard changes)
        this.onWorkflowFormChange.emit(workflowFormValue);
    }

    // If a user sends multiple key after exeding the max length of the task name, we want to show a notification only once in a certain time period
    private lastNotificationTime: number = 0;
    private readonly NOTIFICATION_MAX_LENGTH_COOLDOWN_MS = 5000;

    checkTaskName(value: string): void {
        const now = Date.now();
        if (
            value &&
            value.length === 250 &&
            now - this.lastNotificationTime > this.NOTIFICATION_MAX_LENGTH_COOLDOWN_MS
        ) {
            this.lastNotificationTime = now;
            this.aumicoCoreProvider.dispatchAddNotification(
                this.i18NextService.t('aumico_cpmm_max_length_notice_title') as string,
                this.i18NextService.t('aumico_cpmm_max_length_notice_description') as string,
                NotificationType.ERROR,
            );
        }
    }

    public attemptSave(): Observable<boolean> {
        // 1) finish editing any section names
        for (let i = 0; i < this.sections.length; i++) {
            const section = this.sections.at(i) as FormGroup;
            if (this.sectionNameEditMode[i]) {
                this.editSectionName(section, i);
            }
        }

        // 2) detect empty sections
        const hasEmpty = this.sections.controls.some((c) => (c.get('workflowTasks') as FormArray).length === 0);

        if (hasEmpty) {
            // need confirmation → show dialog and return a subject you’ll resolve later
            this.showEmptySectionConfirmationDialog = true;
            this.saveSubject$ = new Subject<boolean>();
            return this.saveSubject$.asObservable();
        }

        // 3) synchronous validity check
        if (!this.form.valid) {
            this.attemptSaveWasClickedButNotSuccessful = true;
            this.form.markAllAsTouched();
            // Only close all sections if validation fails (to reveal errors)
            this.closeAll();
            return of(false);
        }

        // 4) Form is valid - preserve context and complete save
        this.attemptSaveWasClickedButNotSuccessful = false;

        return of(true);
    }

    private closeAll(): void {
        this.sectionExpanded.fill(false);

        this.accordions.forEach((item) => {
            if (item.isOpened) {
                item.onToggle();
            }
        });
    }

    /** Called when the user clicks “Yes” in your *ngIf dialog */
    public onEmptySectionConfirm() {
        this.showEmptySectionConfirmationDialog = false;
        if (!this.saveSubject$) {
            return;
        }

        // Remove all empty sections (with no tasks)
        for (let i = this.sections.length - 1; i >= 0; i--) {
            const section = this.sections.at(i) as FormGroup;
            const tasks = section.get('workflowTasks') as FormArray;
            if (tasks.length === 0) {
                this.sections.removeAt(i);
            }
        }

        // re-check validity now
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            this.saveSubject$.next(false);
        } else {
            this.saveSubject$.next(true);
        }
        this.saveSubject$.complete();
    }

    /** Called when the user clicks “No” */
    public onEmptySectionCancel() {
        this.showEmptySectionConfirmationDialog = false;
        if (!this.saveSubject$) {
            return;
        }

        this.saveSubject$.next(false);
        this.saveSubject$.complete();
    }

    setActiveType(type: TaskType): void {
        this.activeTaskType = type;

        // re‐attach the new validator
        this.sections.setValidators([uniqueSectionNamesForActiveType(this.activeTaskType)]);

        // force Angular to re‐run it right now
        this.sections.updateValueAndValidity({ onlySelf: true });
    }

    onAccordionExpand(index: number): void {
        this.sectionExpanded[index] = true;
    }

    onAccordionClosed(index: number): void {
        this.sectionExpanded[index] = false;
    }

    public isSectionInvalid(section: AbstractControl): boolean {
        const nameCtrl = section.get('name');
        const tasks = section.get('workflowTasks') as FormArray;

        const invalidByName = nameCtrl?.invalid ?? false;
        const invalidByTasksArray = tasks.invalid;
        const invalidByAnyTask = tasks.controls.some((taskCtrl) => taskCtrl.invalid);

        return invalidByName || invalidByTasksArray || invalidByAnyTask;
    }

    public isAreaInvalid(area: TaskType): boolean {
        return this.sections.controls.some((section) => {
            const tasksArray = section.get('workflowTasks') as FormArray;
            // Only consider sections with at least one task of this area
            const tasksOfType = tasksArray.controls.filter((taskCtrl) => taskCtrl.get('taskType')?.value === area);
            if (tasksOfType.length === 0) {
                return false;
            }
            // Section invalid if name is invalid or any task in that area is invalid
            const nameCtrl = section.get('name');
            const invalidByName = nameCtrl?.invalid ?? false;
            const invalidByAnyTask = tasksOfType.some((taskCtrl) => taskCtrl.invalid);
            return invalidByName || invalidByAnyTask;
        });
    }

    private getTaskInput(sectionIndex: number, taskIndex: number): ElementRef<HTMLInputElement> | undefined {
        // Flatten all task inputs into a list, grouped by section
        let count = 0;
        for (let i = 0; i < this.sections.length; i++) {
            const section = this.sections.at(i) as FormGroup;
            const tasks = section.get('workflowTasks') as FormArray;
            for (let j = 0; j < tasks.length; j++) {
                if (i === sectionIndex && j === taskIndex) {
                    return this.taskDescriptionInputs.get(count);
                }
                count++;
            }
        }
        return undefined;
    }

    /// DRAG & DROP AUTO SCROLL METHODS ///

    onDragMoved(event: any) {
        const edgeThreshold = 60; // px from top/bottom to start scrolling
        const scrollSpeed = 20; // px per interval
        const pointerY = event.pointerPosition.y;
        const scrollContainer = document.getElementById('mainscrollcontent');
        if (!scrollContainer) return;
        const rect = scrollContainer.getBoundingClientRect();

        // Scroll up
        if (pointerY < rect.top + edgeThreshold) {
            this.startAutoScroll(-scrollSpeed, scrollContainer, event);
        }
        // Scroll down
        else if (pointerY > rect.bottom - edgeThreshold) {
            this.startAutoScroll(scrollSpeed, scrollContainer, event);
        } else {
            this.stopAutoScroll();
        }
    }

    startAutoScroll(delta: number, scrollContainer: HTMLElement, dragEvent?: any) {
        if (this.autoScrollInterval) return;
        this.autoScrollInterval = setInterval(() => {
            scrollContainer.scrollTop += delta;
            // Update pointer direction delta for CDK drag
            if (dragEvent && dragEvent.source && dragEvent.source._dragRef && dragEvent.source._dragRef._updatePointerDirectionDelta) {
                dragEvent.source._dragRef._updatePointerDirectionDelta();
            }
            // Simulate mousemove event at current pointer position
            if (dragEvent && dragEvent.pointerPosition) {
                const evt = new MouseEvent('mousemove', {
                    clientX: dragEvent.pointerPosition.x,
                    clientY: dragEvent.pointerPosition.y,
                    bubbles: true
                });
                document.dispatchEvent(evt);
            }
        }, 16); // ~60fps
    }

    stopAutoScroll() {
        if (this.autoScrollInterval) {
            clearInterval(this.autoScrollInterval);
            this.autoScrollInterval = null;
        }
    }

    onDragEnded() {
        this.stopAutoScroll();
    }

}
